/**
 * BTC钱包提供者服务
 * 支持多种BTC钱包：OKX、Unisat、Xverse等
 */

import {
	canConnectWallet,
	detectWalletBrowser,
	getWalletDownloadUrl,
	isMobileDevice,
	openWalletDeepLink,
} from "../utils/mobile-detection";
import { logger } from "@/lib/logger";
import {
	connectLaserEyesWallet,
	disconnectLaserEyesWallet,
	getLaserEyesClient,
	getLaserEyesProviderConstant,
	getLaserEyesStoreState,
} from "@/lib/services/lasereyes-client";
// 使用Edge Runtime兼容的完整ECDSA验证
import { verifyBitcoinSignatureEdge } from "./btc-signature-edge-verification";

export const OKX_CHROME_EXTENSION_URL =
	"https://chromewebstore.google.com/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge";

const normalizeBitcoinAccounts = (accounts: unknown): string[] => {
	if (!Array.isArray(accounts)) {
		return [];
	}

	return accounts
		.map((account) => {
			if (typeof account === "string") {
				return account;
			}

			if (
				account &&
				typeof account === "object" &&
				"address" in account &&
				typeof (account as { address?: unknown }).address === "string"
			) {
				return (account as { address: string }).address;
			}

			return null;
		})
		.filter((account): account is string => typeof account === "string");
};

// 钱包连接结果接口
export interface WalletConnectionResult {
	address: string;
	publicKey?: string;
	accounts?: string[];
	walletProvider: string;
}

// 签名选项
export interface SignMessageOptions {
	signatureType?: "ecdsa" | "bip322-simple";
	addressType?: string;
}

// 通用BTC钱包接口
export interface BTCWalletProvider {
	name: string;
	isAvailable(): boolean;
	connect(): Promise<WalletConnectionResult>;
	getAccounts(): Promise<string[]>;
	signMessage(
		message: string,
		address?: string,
		options?: SignMessageOptions,
	): Promise<string>;
	disconnect(): Promise<void>;
	getPublicKey?(): Promise<string>; // 可选的公钥获取方法
	sendBitcoin(toAddress: string, amount: number): Promise<string>; // 发送BTC，返回交易ID
}

const log = logger.child({ module: "lib/services/btc-wallet-providers" });
const logData = (
	level: "debug" | "info" | "warn" | "error",
	message: string,
	...args: unknown[]
) => {
	if (args.length === 0) {
		log[level](message);
		return;
	}
	if (args.length === 1 && args[0] instanceof Error) {
		log[level](message, { error: args[0] });
		return;
	}
	if (
		args.length === 1 &&
		typeof args[0] === "object" &&
		args[0] !== null &&
		!Array.isArray(args[0])
	) {
		log[level](message, { data: args[0] as Record<string, unknown> });
		return;
	}
	if (args.length === 1) {
		log[level](message, { data: { value: args[0] } });
		return;
	}
	log[level](message, { data: { args } });
};

type OkxBitcoinProvider = {
	connect?: () => Promise<{
		address?: string;
		publicKey?: string;
		accounts?: string[];
	}>;
	requestAccounts?: () => Promise<any[]>;
	getAccounts?: () => Promise<any[]>;
	getPublicKey?: () => Promise<string>;
	signMessage?: (message: string, signatureType?: string) => Promise<string>;
	on?: (event: string, handler: (...args: unknown[]) => void) => void;
	off?: (event: string, handler: (...args: unknown[]) => void) => void;
	removeListener?: (
		event: string,
		handler: (...args: unknown[]) => void,
	) => void;
};

type TokenPocketCompatProvider = {
	isTokenPocket?: boolean;
	requestAccounts?: () => Promise<string[]>;
	getAccounts?: () => Promise<string[]>;
	getPublicKey?: () => Promise<string>;
	signMessage?: (
		message: string,
		signatureType?: string,
	) => Promise<string>;
	sendBitcoin?: (toAddress: string, amount: number) => Promise<string>;
};

type TokenPocketNativeProvider = {
	getWallet?: (params: {
		walletTypes: string[];
		switch?: boolean;
	}) => Promise<{
		result?: boolean;
		data?: {
			address?: string;
			publicKey?: string;
		};
	}>;
	getCurrentWallet?: () => Promise<{
		address?: string;
		publicKey?: string;
		blockchain?: string;
	}>;
	getCurrentBalance?: () => Promise<{
		result?: boolean;
		data?: {
			address?: string;
			balance?: string;
		};
	}>;
	signMessage?: (params: {
		message: string;
		address?: string;
	}) => Promise<{ result?: boolean; data?: string; msg?: string } | string>;
	sign?: (params: {
		data: string;
		address?: string;
	}) => Promise<{ result?: boolean; data?: string; msg?: string } | string>;
	btcTokenTransfer?: (params: {
		from?: string;
		to: string;
		amount: string;
	}) => Promise<any>;
};

export function getOkxBitcoinProvider(): OkxBitcoinProvider | null {
	if (typeof window === "undefined") return null;

	const provider = (window as any).okxwallet?.bitcoin;
	if (!provider || typeof provider !== "object") {
		return null;
	}

	return provider as OkxBitcoinProvider;
}

function isTokenPocketBrowserEnvironment(): boolean {
	if (typeof window === "undefined") return false;
	return (
		detectWalletBrowser() === "tokenpocket" ||
		navigator.userAgent.toLowerCase().includes("tokenpocket")
	);
}

function getTokenPocketCompatProvider(): TokenPocketCompatProvider | null {
	if (typeof window === "undefined") return null;

	const candidates = [
		(window as any).unisat,
		(window as any).bitcoin,
		(window as any).tokenpocket?.bitcoin,
	].filter(Boolean);

	for (const candidate of candidates) {
		if (
			typeof candidate === "object" &&
			(candidate.isTokenPocket || isTokenPocketBrowserEnvironment()) &&
			(typeof candidate.requestAccounts === "function" ||
				typeof candidate.getAccounts === "function")
		) {
			return candidate as TokenPocketCompatProvider;
		}
	}

	return null;
}

function getTokenPocketNativeProvider(): TokenPocketNativeProvider | null {
	if (typeof window === "undefined") return null;

	const tp = (window as any).tp;
	if (!tp || typeof tp !== "object") {
		return null;
	}

	return tp as TokenPocketNativeProvider;
}

export async function waitForOkxBitcoinProvider(
	options: {
		timeoutMs?: number;
		intervalMs?: number;
	} = {},
): Promise<OkxBitcoinProvider | null> {
	const { timeoutMs = 1500, intervalMs = 150 } = options;
	const initialProvider = getOkxBitcoinProvider();
	if (initialProvider || typeof window === "undefined") {
		return initialProvider;
	}

	const startTime = Date.now();

	while (Date.now() - startTime < timeoutMs) {
		await new Promise((resolve) => setTimeout(resolve, intervalMs));
		const provider = getOkxBitcoinProvider();
		if (provider) {
			return provider;
		}
	}

	return null;
}

/**
 * OKX Bitcoin钱包提供者
 */
export class OKXBitcoinWallet implements BTCWalletProvider {
	name = "OKX Wallet";
	private isDesktopLaserEyesReady = false;

	isAvailable(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// PC端检测
			if (!isMobileDevice()) {
				return !!getOkxBitcoinProvider();
			}
		} catch (error) {
			log.warn("Error checking OKX wallet availability", {
				event: "btc_wallet_okx_check_error",
				error,
			});
			return false;
		}

		// 移动端检测
		try {
			const walletBrowser = detectWalletBrowser();
			const hasInjectedWallet = !!(window as any).okxwallet?.bitcoin;

			// 在OKX钱包浏览器中，或有注入对象
			return walletBrowser === "okx" || hasInjectedWallet;
		} catch (error) {
			log.warn("Error in mobile OKX wallet detection", {
				event: "btc_wallet_okx_mobile_check_error",
				error,
			});
			return false;
		}
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			// 移动端处理：如果不在OKX钱包浏览器内且没有注入对象，尝试深链接跳转
			if (
				isMobileDevice() &&
				detectWalletBrowser() !== "okx" &&
				!(window as any).okxwallet?.bitcoin
			) {
				log.info("📱 移动端非OKX环境，尝试深链接跳转", {
					event: "btc_wallet_okx_deeplink_attempt",
				});

				// 使用新的深链接处理函数
				const success = await new Promise<boolean>((resolve) => {
					openWalletDeepLink(
						"okx",
						() => {
							resolve(false);
						},
						{ timeout: 2500 },
					).then(resolve);
				});

				if (!success) {
					const { ios, android } = getWalletDownloadUrl("okx");
					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
					const downloadUrl = isIOS ? ios : android;
					throw new Error(
						`请在OKX钱包中打开此页面，或下载OKX钱包:\n${downloadUrl}`,
					);
				}

				// 如果深链接成功，等待用户返回
				throw new Error("请在OKX钱包中重新打开此页面以完成连接");
			}

			if (!isMobileDevice()) {
				const okxProvider = await getLaserEyesProviderConstant("OKX");
				const { storeState } = await connectLaserEyesWallet(okxProvider, {
					forceFreshState: true,
				});

				if (!storeState.address) {
					throw new Error("No address found in LaserEyes store");
				}

				this.isDesktopLaserEyesReady = true;

				return {
					address: storeState.address,
					publicKey: storeState.publicKey || undefined,
					accounts:
						normalizeBitcoinAccounts(storeState.accounts).length > 0
							? normalizeBitcoinAccounts(storeState.accounts)
							: [storeState.address],
					walletProvider: "okx",
				};
			}

			const okxwallet = await waitForOkxBitcoinProvider({
				timeoutMs: isMobileDevice() ? 600 : 1800,
				intervalMs: 150,
			});

			if (!okxwallet) {
				if (isMobileDevice()) {
					throw new Error("请在OKX钱包中打开此页面，或下载OKX钱包应用");
				} else {
					throw new Error(
						`未检测到 OKX Wallet Chrome 插件。请先安装并刷新页面：${OKX_CHROME_EXTENSION_URL}`,
					);
				}
			}

			// 桌面端恢复为移动端改动前的稳定链路，避免额外的授权/账户归一化逻辑干扰插件状态。
			const result = await okxwallet.connect?.();
			const address = result?.address || "";
			const publicKey = result?.publicKey;

			let accounts: string[] = [];
			if (typeof okxwallet.getAccounts === "function") {
				try {
					const rawAccounts = await okxwallet.getAccounts();
					accounts = Array.isArray(rawAccounts)
						? rawAccounts.filter(
								(account): account is string => typeof account === "string",
							)
						: [];
				} catch (error) {
					logData("warn", "Failed to read OKX accounts after connect", error);
				}
			}

			if (!address) {
				throw new Error("OKX Wallet 已响应，但未返回 Bitcoin 地址。请解锁插件后重试。");
			}

			return {
				address,
				publicKey,
				accounts,
				walletProvider: "okx",
			};
		} catch (error) {
			log.error("❌ OKX Bitcoin wallet connection failed", {
				event: "btc_wallet_okx_connect_error",
				error,
			});
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		if (!isMobileDevice()) {
			const storeState = await getLaserEyesStoreState();
			const accounts = normalizeBitcoinAccounts(storeState.accounts);
			if (accounts.length > 0) {
				return accounts;
			}
			return storeState.address ? [storeState.address] : [];
		}

		const okxwallet = getOkxBitcoinProvider();
		if (!okxwallet || typeof okxwallet.getAccounts !== "function") {
			throw new Error("OKX Bitcoin wallet not found");
		}

		const accounts = await okxwallet.getAccounts();
		return Array.isArray(accounts)
			? accounts.filter(
					(account): account is string => typeof account === "string",
				)
			: [];
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		if (!isMobileDevice()) {
			const client = await getLaserEyesClient();
			const storeState = client.$store.get();
			const signingAddress = address || storeState.address;
			const rawProvider = getOkxBitcoinProvider();
			const signatureType = options.signatureType || "ecdsa";

			if (!signingAddress) {
				throw new Error("OKX LaserEyes wallet not connected");
			}

			if (!rawProvider || typeof rawProvider.signMessage !== "function") {
				throw new Error("OKX Bitcoin wallet not found");
			}

			try {
				return await rawProvider.signMessage(message);
			} catch (error) {
				const reconnectResult = await rawProvider.connect?.();

				try {
					return await rawProvider.signMessage(message);
				} catch (retryError) {
					void reconnectResult;

					return await rawProvider.signMessage(message, signatureType);
				}
			}
		}

		const okxwallet = getOkxBitcoinProvider();
		if (!okxwallet || typeof okxwallet.signMessage !== "function") {
			throw new Error("OKX Bitcoin wallet not found");
		}

		const signatureType = options.signatureType || "ecdsa";

		const signature = await okxwallet.signMessage(message, signatureType);

		return signature;
	}

	async disconnect(): Promise<void> {
		if (!isMobileDevice() && this.isDesktopLaserEyesReady) {
			try {
				await disconnectLaserEyesWallet();
			} catch (error) {
				logData("warn", "Failed to disconnect OKX LaserEyes wallet", error);
			}
			this.isDesktopLaserEyesReady = false;
		}
	}

	async getPublicKey(): Promise<string> {
		if (!isMobileDevice()) {
			const client = await getLaserEyesClient();
			const publicKey = await client.getPublicKey();
			if (!publicKey) {
				throw new Error("Unable to get public key from OKX LaserEyes wallet");
			}
			return publicKey;
		}

		const okxwallet = getOkxBitcoinProvider();
		if (!okxwallet || typeof okxwallet.getPublicKey !== "function") {
			throw new Error("OKX Bitcoin wallet not found");
		}

		return await okxwallet.getPublicKey();
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		if (!isMobileDevice()) {
			const client = await getLaserEyesClient();
			return await client.sendBTC(toAddress, amount);
		}

		const okxbtc = getOkxBitcoinProvider() as any;
		if (!okxbtc) {
			throw new Error("OKX wallet not found");
		}

		try {
			// OKX钱包使用sendBitcoin方法，amount单位是satoshis
			const result = await okxbtc.sendBitcoin(toAddress, amount);
			return result; // 返回交易ID
		} catch (error) {
			log.error("OKX wallet send failed", {
				event: "btc_wallet_okx_send_error",
				error,
			});
			throw error;
		}
	}
}

/**
 * Binance钱包提供者
 * 支持 Chrome 扩展和移动端 Binance App
 * API: window.binancew3w.bitcoin 或 window.unisat (isBinance 兼容模式)
 * 注意: Chrome 扩展目前不支持 Bitcoin，只有 Binance App 内置浏览器支持
 */
export class BinanceWallet implements BTCWalletProvider {
	name = "Binance Wallet";

	/**
	 * 检测是否支持 Bitcoin (用于 SIWB 登录)
	 * Chrome 扩展不支持 Bitcoin，只有 Binance App 内置浏览器支持
	 */
	hasBitcoinSupport(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// 方式1: 原生 Binance Web3 Wallet Bitcoin Provider (App内浏览器)
			if ((window as any).binancew3w?.bitcoin) return true;

			// 方式2: UniSat 兼容模式
			if ((window as any).unisat?.isBinance) return true;

			return false;
		} catch (error) {
			return false;
		}
	}

	/**
	 * 检测是否安装了 Binance Wallet (包括不支持 Bitcoin 的 Chrome 扩展)
	 */
	isInstalled(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// Chrome 扩展 (可能不支持 Bitcoin)
			if ((window as any).binancew3w?.isExtension) return true;

			// 有 Bitcoin 支持的也算安装了
			if (this.hasBitcoinSupport()) return true;

			// 移动端检测
			if (isMobileDevice()) {
				const walletBrowser = detectWalletBrowser();
				if (walletBrowser === "binance") return true;
			}

			return false;
		} catch (error) {
			return false;
		}
	}

	/**
	 * 检测钱包是否可用于 Bitcoin 操作
	 * 对于 SIWB 登录，必须有 Bitcoin 支持
	 */
	isAvailable(): boolean {
		// 对于 Bitcoin 操作，必须有 Bitcoin Provider
		return this.hasBitcoinSupport();
	}

	private getProvider(): { type: string; provider: any } {
		// 方式1: 原生 Binance Web3 Wallet Bitcoin Provider (App内浏览器)
		if ((window as any).binancew3w?.bitcoin) {
			return {
				type: "binancew3w",
				provider: (window as any).binancew3w.bitcoin,
			};
		}

		// 方式2: UniSat 兼容模式
		if ((window as any).unisat?.isBinance) {
			return { type: "unisat", provider: (window as any).unisat };
		}

		// 检测到 Chrome 扩展但不支持 Bitcoin
		if ((window as any).binancew3w?.isExtension) {
			throw new Error(
				"Binance Wallet Chrome 扩展暂不支持 Bitcoin，请使用 Binance App",
			);
		}

		throw new Error("Binance Wallet not found");
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			// 移动端处理：如果不在 Binance App 浏览器内且没有注入对象，尝试深链接跳转
			if (
				isMobileDevice() &&
				detectWalletBrowser() !== "binance" &&
				!this.isAvailable()
			) {
				logData("debug", "📱 移动端非 Binance 环境，尝试深链接跳转...");

				const success = await new Promise<boolean>((resolve) => {
					openWalletDeepLink(
						"binance",
						() => {
							resolve(false);
						},
						{ timeout: 2500 },
					).then(resolve);
				});

				if (!success) {
					const { ios, android } = getWalletDownloadUrl("binance");
					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
					const downloadUrl = isIOS ? ios : android;
					throw new Error(
						`请在 Binance App 中打开此页面，或下载 Binance App:\n${downloadUrl}`,
					);
				}

				throw new Error(
					"请在 Binance App 的 Web3 钱包浏览器中重新打开此页面以完成连接",
				);
			}

			if (!this.isAvailable()) {
				if (isMobileDevice()) {
					throw new Error("请在 Binance App 的 Web3 钱包浏览器中打开此页面");
				} else {
					throw new Error(
						"Binance Wallet not found. Please install Binance Wallet extension from Chrome Web Store.",
					);
				}
			}

			const { type, provider } = this.getProvider();
			logData("debug", `🔗 Binance Wallet connecting via ${type} provider...`);

			// Bitcoin provider (App 内浏览器或 UniSat 兼容模式)
			const accounts = await provider.requestAccounts();
			const address = accounts[0];
			const publicKey = await provider.getPublicKey();

			logData("debug", `✅ Binance Wallet connected: ${address.slice(0, 10)}...`);

			return {
				address,
				publicKey,
				accounts,
				walletProvider: "binance",
			};
		} catch (error) {
			logData("error", "❌ Binance wallet connection failed:", error);
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		if (!this.isAvailable()) {
			throw new Error("Binance wallet not found");
		}

		const { provider } = this.getProvider();
		return await provider.getAccounts();
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Binance wallet not found");
		}

		const { provider } = this.getProvider();
		// Bitcoin provider - 支持 'ecdsa' 和 'bip322-simple'
		const signatureType = options.signatureType || "ecdsa";
		const signature = await provider.signMessage(message, signatureType);
		return signature;
	}

	async disconnect(): Promise<void> {
		// Binance 钱包通常不需要显式断开连接
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Binance wallet not found");
		}

		const { provider } = this.getProvider();

		try {
			// Bitcoin provider - 检查是否有 sendBitcoin 方法
			if (provider.sendBitcoin) {
				const txid = await provider.sendBitcoin(toAddress, amount);
				return txid;
			}
			// Binance 原生 API 需要通过 signPsbt 实现转账
			throw new Error(
				"Binance Wallet 转账功能需要通过 PSBT 签名实现，请使用其他钱包进行转账操作",
			);
		} catch (error) {
			logData("error", "Binance wallet send failed:", error);
			throw error;
		}
	}
}

/**
 * Unisat钱包提供者
 */
export class UnisatWallet implements BTCWalletProvider {
	name = "Unisat Wallet";

	isAvailable(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// PC端检测
			if (!isMobileDevice()) {
				return !!(window as any).unisat;
			}

			// 移动端检测
			const walletBrowser = detectWalletBrowser();
			const hasInjectedWallet = !!(window as any).unisat;

			// 在UniSat钱包浏览器中，或有注入对象
			return walletBrowser === "unisat" || hasInjectedWallet;
		} catch (error) {
			logData("warn", "Error checking Unisat wallet availability:", error);
			return false;
		}
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			// 移动端处理：如果不在UniSat钱包浏览器内且没有注入对象，尝试深链接跳转
			if (
				isMobileDevice() &&
				detectWalletBrowser() !== "unisat" &&
				!(window as any).unisat
			) {
				logData("debug", "📱 移动端非UniSat环境，尝试深链接跳转...");

				// 使用新的深链接处理函数
				const success = await new Promise<boolean>((resolve) => {
					openWalletDeepLink(
						"unisat",
						() => {
							resolve(false);
						},
						{ timeout: 2500 },
					).then(resolve);
				});

				if (!success) {
					const { ios, android } = getWalletDownloadUrl("unisat");
					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
					const downloadUrl = isIOS ? ios : android;
					throw new Error(
						`请在UniSat钱包中打开此页面，或下载UniSat钱包:\n${downloadUrl}`,
					);
				}

				// 如果深链接成功，等待用户返回
				throw new Error("请在UniSat钱包中重新打开此页面以完成连接");
			}

			if (!this.isAvailable()) {
				if (isMobileDevice()) {
					throw new Error("请在UniSat钱包中打开此页面，或下载UniSat钱包应用");
				} else {
					throw new Error(
						"Unisat wallet not found. Please install UniSat Wallet extension.",
					);
				}
			}

			const unisat = (window as any).unisat;

			// 请求连接
			const accounts = await unisat.requestAccounts();
			const address = accounts[0];

			// 获取公钥
			const publicKey = await unisat.getPublicKey();

			return {
				address,
				publicKey,
				accounts,
				walletProvider: "unisat",
			};
		} catch (error) {
			logData("error", "❌ Unisat wallet connection failed:", error);
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		if (!this.isAvailable()) {
			throw new Error("Unisat wallet not found");
		}

		const unisat = (window as any).unisat;
		return await unisat.getAccounts();
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Unisat wallet not found");
		}

		const unisat = (window as any).unisat;
		const signature = await unisat.signMessage(message);
		return signature;
	}

	async disconnect(): Promise<void> {
		// Unisat钱包断开连接
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Unisat wallet not found");
		}

		const unisat = (window as any).unisat;

		try {
			// Unisat钱包使用sendBitcoin方法，amount单位是satoshis
			const txid = await unisat.sendBitcoin(toAddress, amount);
			return txid; // 返回交易ID
		} catch (error) {
			logData("error", "Unisat wallet send failed:", error);
			throw error;
		}
	}
}

/**
 * Xverse钱包提供者 (使用LaserEyes)
 */
export class XverseWallet implements BTCWalletProvider {
	name = "Xverse Wallet";

	isAvailable(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// PC端检测：LaserEyes会处理钱包检测
			if (!isMobileDevice()) {
				return true; // LaserEyes会在连接时检测
			}

			// 移动端检测
			const walletBrowser = detectWalletBrowser();
			const hasInjectedWallet = !!(window as any).XverseProviders
				?.BitcoinProvider;

			// 在Xverse钱包浏览器中，或有注入对象，或LaserEyes可以处理
			return walletBrowser === "xverse" || hasInjectedWallet || true;
		} catch (error) {
			logData("warn", "Error checking Xverse wallet availability:", error);
			return false;
		}
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			// 移动端处理：如果不在Xverse钱包浏览器内，尝试深链接跳转
			if (isMobileDevice()) {
				const walletBrowser = detectWalletBrowser();
				const hasXverseProvider = !!(window as any).XverseProviders
					?.BitcoinProvider;

				// 如果不在Xverse钱包浏览器内，且没有Xverse Provider
				if (walletBrowser !== "xverse" && !hasXverseProvider) {
					logData("debug", "📱 移动端非Xverse环境，尝试深链接跳转...");

					// 使用新的深链接处理函数
					const success = await new Promise<boolean>((resolve) => {
						openWalletDeepLink(
							"xverse",
							() => {
								resolve(false);
							},
							{ timeout: 2500 },
						).then(resolve);
					});

					if (!success) {
						const { ios, android } = getWalletDownloadUrl("xverse");
						const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
						const downloadUrl = isIOS ? ios : android;
						throw new Error(
							`请在Xverse钱包中打开此页面，或下载Xverse钱包:\n${downloadUrl}`,
						);
					}

					// 如果深链接成功，等待用户返回
					throw new Error("请在Xverse钱包中重新打开此页面以完成连接");
				}
			}

			const xverseProvider = await getLaserEyesProviderConstant("XVERSE");
			const { storeState } = await connectLaserEyesWallet(xverseProvider);

			if (!storeState.address) {
				throw new Error("No address found in LaserEyes store");
			}

			const address = storeState.address;
			let publicKey = storeState.publicKey || "";

			// 基于参考代码，确保公钥格式正确
			if (publicKey) {
				// 确保公钥是正确的格式
				if (address.startsWith("bc1p")) {
					// Taproot地址：使用x-only公钥格式（64字符，无前缀）
					if (
						publicKey.length === 66 &&
						(publicKey.startsWith("02") || publicKey.startsWith("03"))
					) {
						// 压缩格式，转换为x-only格式
						publicKey = publicKey.slice(2);
					}
				} else if (address.startsWith("bc1q")) {
					// SegWit地址：使用压缩公钥
					if (publicKey.length === 64) {
						publicKey = "02" + publicKey;
					}
				}
			} else {
				// 如果LaserEyes没有返回公钥，尝试直接从Xverse获取
				try {
					const bitcoinProvider = (window as any).XverseProviders
						?.BitcoinProvider;
					if (bitcoinProvider) {
						const xverseResult = await bitcoinProvider.request(
							"getAccounts",
							{},
						);
						if (xverseResult && xverseResult[0] && xverseResult[0].publicKey) {
							publicKey = xverseResult[0].publicKey;
						}
					}
				} catch (e) {
					// 静默处理错误
				}
			}

			// 获取账户列表
			const accounts = [address];

			return {
				address,
				publicKey,
				accounts,
				walletProvider: "xverse",
			};
		} catch (error) {
			logData("error", "❌ Xverse wallet connection failed:", error);
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		try {
			const storeState = await getLaserEyesStoreState();
			return storeState.address ? [storeState.address] : [];
		} catch (error) {
			logData("error", "Failed to get accounts:", error);
			return [];
		}
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		try {
			// 根据参考代码，LaserEyes的signMessage需要传递message和address参数
			let signature;
			try {
				const client = await getLaserEyesClient();

				// 如果没有传递地址，从store获取
				if (!address) {
					const storeState = client.$store.get();
					address = storeState.address;
				}

				if (!address) {
					throw new Error("No address available for signing");
				}

				// 使用参考代码的方式：signMessage(message, address)
				signature = await client.signMessage(message, address);
			} catch (laserEyesError) {
				// 回退到直接使用Xverse API
				const bitcoinProvider = (window as any).XverseProviders
					?.BitcoinProvider;
				if (!bitcoinProvider) {
					throw new Error(
						"Neither LaserEyes nor Xverse Bitcoin provider available for signing",
					);
				}

				// 请求签名
				const signatureResponse = await bitcoinProvider.request("signMessage", {
					address: address,
					message: message,
				});

				signature = signatureResponse?.signature;
				if (!signature) {
					throw new Error("Signature not received from Xverse");
				}
			}

			return signature;
		} catch (error) {
			logData("error", "❌ Xverse message signing failed:", error);
			throw error;
		}
	}

	// 添加getPublicKey方法，参考代码中的实现
	async getPublicKey(): Promise<string> {
		try {
			// 根据参考代码，使用client.getPublicKey()方法
			const client = await getLaserEyesClient();
			const publicKey = await client.getPublicKey();
			return publicKey;
		} catch (error) {
			// 回退到从store获取
			const storeState = await getLaserEyesStoreState();
			if (storeState.publicKey) {
				return storeState.publicKey;
			}

			throw new Error("Unable to get public key from Xverse wallet");
		}
	}

	async disconnect(): Promise<void> {
		try {
			await disconnectLaserEyesWallet();
		} catch {}
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		try {
			// LaserEyes使用sendBTC方法，amount单位是satoshis
			const client = await getLaserEyesClient();
			const result = await client.sendBTC(toAddress, amount);
			return result; // 返回交易ID
		} catch (error) {
			logData("error", "Xverse wallet send failed:", error);
			throw error;
		}
	}
}

/**
 * Phantom钱包提供者
 */
export class PhantomWallet implements BTCWalletProvider {
	name = "Phantom Wallet";

	isAvailable(): boolean {
		if (typeof window === "undefined") return false;

		try {
			// PC端检测
			if (!isMobileDevice()) {
				return !!(window as any).phantom?.bitcoin;
			}

			// 移动端检测
			const walletBrowser = detectWalletBrowser();
			const hasInjectedWallet = !!(window as any).phantom?.bitcoin;

			// 在Phantom钱包浏览器中，或有注入对象
			return walletBrowser === "phantom" || hasInjectedWallet;
		} catch (error) {
			logData("warn", "Error checking Phantom wallet availability:", error);
			return false;
		}
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			// 移动端处理：Phantom钱包Bitcoin功能的特殊处理
			if (
				isMobileDevice() &&
				detectWalletBrowser() !== "phantom" &&
				!(window as any).phantom?.bitcoin
			) {
				logData("debug", "📱 移动端非Phantom环境，尝试引导用户到APP...");

				// 尝试深链接跳转，但提供清晰的指引
				const success = await new Promise<boolean>((resolve) => {
					openWalletDeepLink(
						"phantom",
						() => {
							resolve(false);
						},
						{ timeout: 2500 },
					).then(resolve);
				});

				const { ios, android } = getWalletDownloadUrl("phantom");
				const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
				const downloadUrl = isIOS ? ios : android;

				if (!success) {
					throw new Error(`请在Phantom钱包APP中打开此页面：
1. 打开Phantom钱包APP
2. 点击底部的浏览器图标
3. 在地址栏输入此网站地址
4. 或下载Phantom钱包: ${downloadUrl}`);
				}

				// 如果深链接成功，指导用户在APP中完成操作
				throw new Error(
					"请在Phantom钱包APP的浏览器中重新打开此页面以完成Bitcoin连接",
				);
			}

			if (!this.isAvailable()) {
				if (isMobileDevice()) {
					throw new Error("请在Phantom钱包中打开此页面，或下载Phantom钱包应用");
				} else {
					throw new Error(
						"Phantom Bitcoin wallet not found. Please install Phantom Wallet extension.",
					);
				}
			}

			const phantom = (window as any).phantom;

			// 连接Phantom钱包的Bitcoin功能
			const result = await phantom.bitcoin.requestAccounts();

			if (!result || result.length === 0) {
				throw new Error("No accounts returned from Phantom wallet");
			}

			const account = result[0];
			const { address, publicKey, addressType } = account;

			// 获取所有账户
			const accounts = result.map((acc: any) => acc.address);

			return {
				address,
				publicKey,
				accounts,
				walletProvider: "phantom",
			};
		} catch (error) {
			logData("error", "❌ Phantom Bitcoin wallet connection failed:", error);
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		if (!this.isAvailable()) {
			throw new Error("Phantom Bitcoin wallet not found");
		}

		const phantom = (window as any).phantom;
		const result = await phantom.bitcoin.requestAccounts();
		return result.map((acc: any) => acc.address);
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Phantom Bitcoin wallet not found");
		}

		const phantom = (window as any).phantom;

		// 如果没有提供地址，获取当前连接的地址
		if (!address) {
			const accounts = await this.getAccounts();
			if (accounts.length === 0) {
				throw new Error("No accounts available for signing");
			}
			address = accounts[0];
		}

		try {
			// 根据Phantom官方文档，signMessage需要Uint8Array格式的消息
			const messageBytes = new TextEncoder().encode(message);

			// 使用Phantom官方文档的正确格式: signMessage(address, Uint8Array)
			const result = await phantom.bitcoin.signMessage(address, messageBytes);

			if (!result || !result.signature) {
				throw new Error("Failed to get signature from Phantom wallet");
			}

			// 根据Phantom官方文档，signature字段应该是Uint8Array
			const signatureBytes = new Uint8Array(result.signature);
			const signature = Buffer.from(signatureBytes).toString("base64");

			return signature;
		} catch (error) {
			logData("error", "❌ Phantom签名失败:", error);
			throw error;
		}
	}

	async disconnect(): Promise<void> {
		// Phantom钱包通常不需要显式断开连接
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("Phantom Bitcoin wallet not found");
		}

		const phantom = (window as any).phantom;

		try {
			// 获取当前账户
			const accounts = await this.getAccounts();
			if (accounts.length === 0) {
				throw new Error("No accounts available for sending");
			}

			// 构建交易请求
			// Phantom Bitcoin API 使用 signAndSendTransaction
			const transaction = {
				to: toAddress,
				value: amount, // satoshis
				from: accounts[0],
			};

			// Phantom钱包的Bitcoin API可能需要构建PSBT
			// 根据Phantom文档，使用signAndSendTransaction
			const result = await phantom.bitcoin.signAndSendTransaction(transaction);

			if (result && result.signature) {
				// 返回交易ID
				return result.signature;
			} else {
				throw new Error("Failed to send Bitcoin via Phantom wallet");
			}
		} catch (error) {
			logData("error", "Phantom wallet send failed:", error);
			throw error;
		}
	}
}

/**
 * TokenPocket钱包提供者
 */
export class TokenPocketWallet implements BTCWalletProvider {
	name = "TokenPocket Wallet";

	isAvailable(): boolean {
		if (typeof window === "undefined") return false;

		if (isTokenPocketBrowserEnvironment()) {
			return true;
		}

		if (!isMobileDevice()) {
			return false;
		}

		return Boolean(
			getTokenPocketCompatProvider() ||
				getTokenPocketNativeProvider() ||
				(window as any).tokenpocket?.bitcoin,
		);
	}

	async connect(): Promise<WalletConnectionResult> {
		try {
			if (
				isMobileDevice() &&
				!isTokenPocketBrowserEnvironment() &&
				!getTokenPocketCompatProvider() &&
				!getTokenPocketNativeProvider()
			) {
				const success = await new Promise<boolean>((resolve) => {
					openWalletDeepLink(
						"tokenpocket",
						() => {
							resolve(false);
						},
						{ timeout: 2500 },
					).then(resolve);
				});

				if (!success) {
					const { ios, android } = getWalletDownloadUrl("tokenpocket");
					const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
					const downloadUrl = isIOS ? ios : android;
					throw new Error(
						`请在TokenPocket钱包中打开此页面，或下载TokenPocket钱包:\n${downloadUrl}`,
					);
				}

				// 如果深链接成功，等待用户返回
				throw new Error("请在TokenPocket钱包中重新打开此页面以完成连接");
			}

			if (!this.isAvailable()) {
				if (isMobileDevice()) {
					throw new Error(
						"请在TokenPocket钱包中打开此页面，或下载TokenPocket钱包应用",
					);
				} else {
					throw new Error(
						"TokenPocket Bitcoin wallet not found. TokenPocket is primarily a mobile wallet.",
					);
				}
			}

			const compatProvider = getTokenPocketCompatProvider();
			if (compatProvider) {
				const accounts =
					typeof compatProvider.requestAccounts === "function"
						? await compatProvider.requestAccounts()
						: await compatProvider.getAccounts?.();
				const address = accounts?.[0];

				if (!address) {
					throw new Error("TokenPocket 未返回 BTC 地址，请确认当前在 BTC 钱包中。");
				}

				const publicKey =
					typeof compatProvider.getPublicKey === "function"
						? await compatProvider.getPublicKey().catch(() => "")
						: "";

				return {
					address,
					publicKey,
					accounts,
					walletProvider: "tokenpocket",
				};
			}

			const tp = getTokenPocketNativeProvider();
			if (tp?.getWallet) {
				const walletResult = await tp.getWallet({
					walletTypes: ["btc"],
					switch: false,
				});

				if (walletResult?.result && walletResult.data?.address) {
					return {
						address: walletResult.data.address,
						publicKey: walletResult.data.publicKey || "",
						accounts: [walletResult.data.address],
						walletProvider: "tokenpocket",
					};
				}
			}

			if (tp?.getCurrentWallet) {
				const currentWallet = await tp.getCurrentWallet();
				if (currentWallet?.address) {
					return {
						address: currentWallet.address,
						publicKey: currentWallet.publicKey || "",
						accounts: [currentWallet.address],
						walletProvider: "tokenpocket",
					};
				}
			}

			throw new Error(
				"无法获取 TokenPocket BTC 地址。请确认您在 TP 钱包内置浏览器的 BTC 钱包环境中。",
			);
		} catch (error) {
			logData("error", "❌ TokenPocket Bitcoin wallet connection failed:", error);
			throw error;
		}
	}

	async getAccounts(): Promise<string[]> {
		if (!this.isAvailable()) {
			throw new Error("TokenPocket Bitcoin wallet not found");
		}

		const compatProvider = getTokenPocketCompatProvider();
		if (compatProvider?.getAccounts) {
			return await compatProvider.getAccounts();
		}
		if (compatProvider?.requestAccounts) {
			return await compatProvider.requestAccounts();
		}

		const tp = getTokenPocketNativeProvider();
		if (tp?.getCurrentWallet) {
			const wallet = await tp.getCurrentWallet();
			return wallet?.address ? [wallet.address] : [];
		}

		return [];
	}

	async signMessage(
		message: string,
		address?: string,
		options: SignMessageOptions = {},
	): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("TokenPocket Bitcoin wallet not found");
		}

		const compatProvider = getTokenPocketCompatProvider();
		if (compatProvider?.signMessage) {
			try {
				return await compatProvider.signMessage(
					message,
					options.signatureType,
				);
			} catch (error) {
				if (options.signatureType) {
					return await compatProvider.signMessage(message);
				}
				throw error;
			}
		}

		const tp = getTokenPocketNativeProvider();
		if (tp?.signMessage) {
			const result = await tp.signMessage({
				message,
				address: address || "",
			});
			if (typeof result === "string") {
				return result;
			}
			if (result?.result && (result.data || result.msg)) {
				return result.data || result.msg || "";
			}
		}

		if (tp?.sign) {
			const result = await tp.sign({
				data: message,
				address: address || "",
			});
			if (typeof result === "string") {
				return result;
			}
			if (result?.result && (result.data || result.msg)) {
				return result.data || result.msg || "";
			}
		}

		throw new Error(
			"TokenPocket 当前环境不可用于 BTC 消息签名，请在 TP 内置浏览器的 BTC 钱包环境中重试。",
		);
	}

	async disconnect(): Promise<void> {
		// TokenPocket钱包通常不需要显式断开连接
	}

	async getPublicKey(): Promise<string> {
		const compatProvider = getTokenPocketCompatProvider();
		if (compatProvider?.getPublicKey) {
			return await compatProvider.getPublicKey();
		}

		const tp = getTokenPocketNativeProvider();
		if (tp?.getCurrentWallet) {
			const currentWallet = await tp.getCurrentWallet();
			if (currentWallet?.publicKey) {
				return currentWallet.publicKey;
			}
		}

		throw new Error("TokenPocket BTC public key not found");
	}

	async sendBitcoin(toAddress: string, amount: number): Promise<string> {
		if (!this.isAvailable()) {
			throw new Error("TokenPocket wallet not found");
		}

		logData("debug", "💸 开始发送BTC:", {
			toAddress,
			amount,
			amountBTC: amount / 1e8,
		});

		// 首先尝试UniSat兼容模式
		const windowObj = window as any;
		const compatObj = windowObj.bitcoin || windowObj.unisat;

		if (compatObj && compatObj.isTokenPocket && compatObj.sendBitcoin) {
			logData("debug", "🔄 使用TokenPocket的UniSat兼容模式发送BTC...");
			try {
				const txid = await compatObj.sendBitcoin(toAddress, amount);
				logData("debug", "✅ UniSat兼容模式发送成功，交易ID:", txid);
				return txid;
			} catch (compatError) {
				logData("warn", 
					"UniSat兼容模式发送失败，尝试原生TokenPocket方法:",
					compatError,
				);
			}
		}

		const tp = windowObj.tp;

		if (!tp) {
			throw new Error(
				"TokenPocket JavaScript SDK (tp) not found. 请尝试：\n1. 确保在TokenPocket的DApp浏览器中打开\n2. 刷新页面重试\n3. 检查TokenPocket版本是否支持BTC功能",
			);
		}

		try {
			let result;

			if (tp.btcTokenTransfer) {
				// 使用TokenPocket官方SDK的BTC转账方法
				logData("debug", "📤 使用tp.btcTokenTransfer方法");

				// 根据官方文档，需要获取当前地址作为from参数
				let fromAddress = "";
				try {
					const walletInfo = await tp.getCurrentWallet();
					if (walletInfo && walletInfo.address) {
						fromAddress = walletInfo.address;
					}
				} catch (e) {
					logData("warn", "无法获取from地址，尝试其他方式");
				}

				// 如果仍然没有from地址，尝试使用余额API的响应
				if (!fromAddress) {
					try {
						const balanceResponse = await tp.getCurrentBalance();
						// 有些实现可能在余额响应中包含地址信息
						if (
							balanceResponse &&
							balanceResponse.data &&
							balanceResponse.data.address
						) {
							fromAddress = balanceResponse.data.address;
						}
					} catch (e) {
						logData("warn", "无法通过余额API获取地址");
					}
				}

				// 根据官方文档格式调用btcTokenTransfer
				const transferParams = {
					from: fromAddress || "", // 发送方地址，如果为空TokenPocket可能会自动填充
					to: toAddress, // 接收方地址
					amount: (amount / 1e8).toString(), // 转换为BTC单位的字符串
				};

				logData("debug", "📤 转账参数:", transferParams);
				result = await tp.btcTokenTransfer(transferParams);
			} else {
				throw new Error(
					"TokenPocket btcTokenTransfer方法不可用。\n\n可能原因：\n1. 当前TokenPocket版本不支持BTC转账\n2. 未在BTC钱包模式下\n3. 需要更新TokenPocket应用\n\n请检查您的TokenPocket设置并重试。",
				);
			}

			logData("debug", "📤 转账结果:", result);

			// 根据官方文档，成功的响应格式为：
			// { result: true, data: 'transaction_id', msg: 'transaction_id' }
			let txid;

			if (result && result.result === true) {
				// 成功响应
				txid = result.data || result.msg;
			} else if (result && typeof result === "string") {
				// 直接返回交易ID字符串
				txid = result;
			} else {
				logData("error", "❌ 意外的转账响应格式:", result);
				throw new Error("转账失败：响应格式异常");
			}

			if (!txid || txid === "") {
				throw new Error("转账失败：未收到交易ID");
			}

			logData("debug", "✅ BTC转账成功，交易ID:", txid);
			return txid;
		} catch (error) {
			logData("error", "❌ TokenPocket BTC转账失败:", error);

			// 提供更有用的错误信息
			if (
				error instanceof Error &&
				error.message &&
				error.message.includes("insufficient")
			) {
				throw new Error(
					"转账失败：余额不足。请检查您的BTC余额是否足够支付转账金额和网络费用。",
				);
			} else if (
				error instanceof Error &&
				error.message &&
				error.message.includes("address")
			) {
				throw new Error("转账失败：地址格式错误。请检查接收地址是否正确。");
			} else if (
				error instanceof Error &&
				error.message &&
				error.message.includes("amount")
			) {
				throw new Error("转账失败：金额无效。请检查转账金额是否正确。");
			} else {
				throw error;
			}
		}
	}
}

/**
 * 钱包工厂 - 根据类型创建钱包实例
 */
export class WalletFactory {
	private static wallets = new Map<string, BTCWalletProvider>([
		["okx", new OKXBitcoinWallet()],
		["binance", new BinanceWallet()],
		["unisat", new UnisatWallet()],
		["xverse", new XverseWallet()],
		["phantom", new PhantomWallet()],
		["tokenpocket", new TokenPocketWallet()],
	]);

	static getWallet(type: string): BTCWalletProvider {
		const wallet = WalletFactory.wallets.get(type.toLowerCase());
		if (!wallet) {
			throw new Error(`Unsupported wallet type: ${type}`);
		}
		return wallet;
	}

	static getSupportedWallets(): Array<{
		id: string;
		name: string;
		available: boolean;
	}> {
		return Array.from(WalletFactory.wallets.entries()).map(([id, wallet]) => {
			let available = false;
			try {
				available = wallet.isAvailable();
			} catch (error) {
				logData("warn", `⚠️ 检测钱包 ${id} 可用性时发生错误:`, error);
				available = false;
			}
			return {
				id,
				name: wallet.name,
				available,
			};
		});
	}

	static getAvailableWallets(): Array<{ id: string; name: string }> {
		return WalletFactory.getSupportedWallets()
			.filter((wallet) => wallet.available)
			.map(({ id, name }) => ({ id, name }));
	}
}

/**
 * 通用钱包连接函数 (支持移动端)
 * 返回连接结果和钱包实例
 */
export async function connectBitcoinWallet(
	walletType: string,
): Promise<WalletConnectionResult & { wallet: BTCWalletProvider }> {
	try {
		const wallet = WalletFactory.getWallet(walletType);

		// 移动端特殊处理
		if (isMobileDevice()) {
			// 如果不在目标钱包浏览器中且没有注入对象，提示用户
			if (!wallet.isAvailable() && !canConnectWallet(walletType)) {
				const { ios, android } = getWalletDownloadUrl(walletType);
				const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
				const downloadUrl = isIOS ? ios : android;

				throw new Error(
					`请在${wallet.name}中打开此页面，或下载${wallet.name}应用:\n${downloadUrl}`,
				);
			}
		}

		if (!wallet.isAvailable()) {
			if (isMobileDevice()) {
				throw new Error(
					`${wallet.name} 不可用。请在对应钱包应用中打开此页面。`,
				);
			} else {
				throw new Error(
					`${wallet.name} is not available. Please install the wallet extension.`,
				);
			}
		}

		const result = await wallet.connect();
		return { ...result, wallet }; // 返回连接结果和钱包实例
	} catch (error) {
		logData("error", "❌ Bitcoin wallet connection failed:", error);
		throw error;
	}
}

/**
 * 生成Bitcoin签名消息
 */
export function generateBitcoinSignMessage(
	address: string,
	walletProvider: string,
): string {
	const timestamp = new Date().toISOString();
	const nonce = Math.random().toString(36).substring(2, 15);

	return `Welcome to Tyche Baby!

Please sign this message to verify your Bitcoin wallet ownership.

Wallet: ${walletProvider.toUpperCase()}
Address: ${address}
Nonce: ${nonce}
Timestamp: ${timestamp}

This request will not trigger any blockchain transactions or incur any fees.`;
}

/**
 * BTC签名验证 (需要实现具体的验证逻辑)
 */
export async function verifyBitcoinSignature(
	address: string,
	message: string,
	signature: string,
	walletType: string,
): Promise<boolean> {
	try {
		logData("debug", "🔍 Verifying Bitcoin signature:", {
			wallet: walletType,
			address: address.slice(0, 10) + "...",
			messageLength: message.length,
			signatureLength: signature.length,
			signature: signature.slice(0, 20) + "...",
		});

		// 基本格式检查
		if (!address || !message || !signature) {
			logData("error", 
				"❌ Missing required fields for Bitcoin signature verification",
			);
			return false;
		}

		// 验证地址格式
		if (!isValidBitcoinAddress(address)) {
			logData("error", "❌ Invalid Bitcoin address format:", address);
			return false;
		}

		// 检查消息时间戳（防重放攻击）
		const timestampMatch = message.match(/Timestamp: (.+?)(?:\n|$)/);
		if (timestampMatch) {
			const timestamp = new Date(timestampMatch[1]);
			const now = new Date();
			const timeDiff = now.getTime() - timestamp.getTime();

			// 消息必须在10分钟内
			if (timeDiff > 10 * 60 * 1000) {
				logData("error", "❌ Bitcoin signature timestamp expired:", {
					timestamp: timestamp.toISOString(),
					now: now.toISOString(),
					diffMinutes: Math.round(timeDiff / 1000 / 60),
				});
				return false;
			}
		}

		// 验证消息是否包含正确的地址
		if (!message.includes(address)) {
			logData("error", "❌ Message does not contain the correct address");
			return false;
		}

		// 使用Edge Runtime兼容的完整Bitcoin ECDSA签名验证
		try {
			const isValid = await verifyBitcoinSignatureEdge(
				address,
				message,
				signature,
			);

			if (!isValid) {
				logData("error", "❌ Bitcoin signature verification failed");
				return false;
			}

			logData("debug", "✅ Bitcoin signature verification passed");
			return true;
		} catch (error) {
			logData("error", "❌ Bitcoin signature verification error:", error);
			return false;
		}
	} catch (error) {
		logData("error", "❌ Bitcoin signature verification failed:", error);
		return false;
	}
}

// 验证Bitcoin地址格式的辅助函数
export function isValidBitcoinAddress(address: string): boolean {
	// 基本的Bitcoin地址格式检查
	if (!address || typeof address !== "string") {
		return false;
	}

	// Legacy地址 (以1开头)
	if (address.startsWith("1") && address.length >= 26 && address.length <= 35) {
		return /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/.test(
			address,
		);
	}

	// Script地址 (以3开头)
	if (address.startsWith("3") && address.length >= 26 && address.length <= 35) {
		return /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/.test(
			address,
		);
	}

	// Bech32 SegWit地址 (以bc1q开头)
	if (address.startsWith("bc1q") && address.length === 42) {
		return /^bc1[02-9ac-hj-np-z]+$/.test(address);
	}

	// Bech32 Taproot地址 (以bc1p开头)
	if (address.startsWith("bc1p") && address.length === 62) {
		return /^bc1[02-9ac-hj-np-z]+$/.test(address);
	}

	return false;
}

/**
 * 统一的Bitcoin地址类型检测函数
 * 用于登录和关联时的地址类型检测
 */
export function detectBitcoinAddressType(address: string): string {
	if (!address || typeof address !== "string") {
		return "unknown";
	}

	if (address.startsWith("1")) return "legacy";
	if (address.startsWith("3")) return "script";
	if (address.startsWith("bc1q")) return "segwit";
	if (address.startsWith("bc1p")) return "taproot";
	return "unknown";
}
